import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type StaffingDestinationJSON = z.infer<typeof StaffingDestinationSchema.StaffingDestinationJSONZ>;
export type StaffingDestinationParams = z.infer<typeof StaffingDestinationSchema.StaffingDestinationZ>;
export type StaffingDestinationKind = StaffingDestinationParams["kind"];
/** Which event the destination's date is the date of — see `StaffingDestinationMilestones`. */
export type StaffingDestinationMilestone = StaffingDestinationParams["activeSinceEvent"];

function hasVersionForDestinationKind(destination: { kind: string; version: number | null }): boolean {
  return destination.kind === "Proposal"
    ? destination.version === null
    : destination.version !== null;
}

/**
 * One piece of work the signed-in viewer may put somebody onto.
 *
 * Deliberately narrower than a proposal or a work agreement: the API answers the Add to Work picker
 * with a name, which kind of work it is, and whether the person is already on its team, and nothing
 * about the terms. Parsing it as a proposal would invent an agreement the server never sent.
 *
 * The two kinds are spelled exactly as the API serializes them, so a value that drifts fails at the
 * boundary rather than rendering as an entry nobody can act on.
 */
export default class StaffingDestinationSchema {
  public static readonly StaffingDestinationJSONZ = z.object({
    id: z.uuid(),
    kind: z.enum(["Proposal", "WorkAgreement"]),
    name: z.string(),
    /**
     * The organization on the other side, or an empty string where there is nobody to name.
     *
     * Empty rather than absent, because a proposal that has not been addressed to a vendor yet
     * genuinely has no counterparty and the picker says so in words. A schema that made this
     * optional would let a server that simply stopped sending it look the same as one answering
     * honestly.
     */
    counterpartyName: z.string(),
    activeSince: DateSchema.DateJSONZ,
    activeSinceEvent: z.enum(["Created", "Hired"]),
    candidateAlreadyBelongs: z.boolean(),
    version: z.number().int().positive().nullable(),
  }).refine(hasVersionForDestinationKind, {
    message: "Only work agreements carry a version, and every work agreement has one.",
    path: ["version"],
  });

  public static readonly StaffingDestinationZ = z.object({
    id: z.instanceof(Guid),
    kind: z.enum(["Proposal", "WorkAgreement"]),
    name: z.string(),
    counterpartyName: z.string(),
    activeSince: DateSchema.type,
    activeSinceEvent: z.enum(["Created", "Hired"]),
    candidateAlreadyBelongs: z.boolean(),
    version: z.number().int().positive().nullable(),
  }).refine(hasVersionForDestinationKind, {
    message: "Only work agreements carry a version, and every work agreement has one.",
    path: ["version"],
  });

  public static validate(values: unknown): StaffingDestinationParams {
    return parseOrThrow(StaffingDestinationSchema.StaffingDestinationZ, values, {
      logError: true,
      schemaName: "StaffingDestinationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): StaffingDestinationJSON {
    return parseOrThrow(StaffingDestinationSchema.StaffingDestinationJSONZ, json, {
      logError: true,
      schemaName: "StaffingDestinationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is StaffingDestinationParams {
    try {
      StaffingDestinationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is StaffingDestinationJSON {
    try {
      StaffingDestinationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: StaffingDestinationJSON): StaffingDestinationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(StaffingDestinationSchema.StaffingDestinationJSONZ, json, {
      logError: true,
      schemaName: "StaffingDestinationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return StaffingDestinationSchema.codec.decode(parsed);
  }

  public static serialize(params: StaffingDestinationParams): StaffingDestinationJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = StaffingDestinationSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return StaffingDestinationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    StaffingDestinationSchema.StaffingDestinationJSONZ,
    StaffingDestinationSchema.StaffingDestinationZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        kind: data.kind,
        name: data.name,
        counterpartyName: data.counterpartyName,
        activeSince: AHDate.fromJSON(data.activeSince)!,
        activeSinceEvent: data.activeSinceEvent,
        candidateAlreadyBelongs: data.candidateAlreadyBelongs,
        version: data.version,
      }),
      encode: (destination) => ({
        id: destination.id.toJSON(),
        kind: destination.kind,
        name: destination.name,
        counterpartyName: destination.counterpartyName,
        activeSince: destination.activeSince.toJSON(),
        activeSinceEvent: destination.activeSinceEvent,
        candidateAlreadyBelongs: destination.candidateAlreadyBelongs,
        version: destination.version,
      }),
    }
  );
}
